﻿import { useEffect, useMemo, useRef, useState } from "react";
import {
  Building2,
  GraduationCap,
  UserRound,
  Award,
  Network,
  LayoutDashboard,
  Database,
  Users,
  Zap,
  Trophy,
  ChevronDown,
  AlertTriangle,
  RotateCw,
} from "lucide-react";
import {
  Chart as ChartJS,
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import AdminLayout from "../../layouts/AdminLayout";
import StatusDot from "../../components/common/StatusDot";
import BoxLinksPanel from "../../components/common/BoxLinksPanel";
import { useAuth } from "../../context/AuthContext";
import "../../assets/styles/colors.css";
import {
  getUsersOrganizations,
  normalizeUsersOrganizations,
  getPlatformAnalytics,
  normalizePlatformAnalytics,
  getInfrastructure,
  normalizeInfrastructure,
  getTopologyUsage,
  normalizeTopologyUsage,
  getActivityDeployments,
  normalizeActivityDeployments,
  getPendingApprovals,
  normalizePendingApprovals,
  getTeamChallenges,
  normalizeTeamChallenges,
  getTopPerformingCompanies,
  normalizeTopPerformingCompanies,
} from "../../api/services/platformAdminDashboardService";

ChartJS.register(
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
);

/* ─────────────────────────────────────────────
   Theme bridge
   ───────────────────────────────────────────── */
function useThemeColors() {
  const [c, setC] = useState(null);
  // Chart.js needs concrete colour strings, so we snapshot the computed
  useEffect(() => {
    const readColors = () => {
      const root = getComputedStyle(document.documentElement);
      const v = (name, fallback) =>
        (root.getPropertyValue(name) || "").trim() || fallback;
      setC({
        primary: v("--color-primary", "#00e639"),
        primaryHover: v("--color-primary-hover", "#00c430"),
        accentViolet: v("--color-accent-violet", "#8b5cf6"),
        textPrimary: v("--color-text-primary", "#dfe2eb"),
        textSecondary: v("--color-text-secondary", "#8fa3be"),
        textMuted: v("--color-text-muted", "#3e4f65"),
        border: v("--color-border", "#212a38"),
        surface: v("--color-surface", "#181c22"),
        ringTrack: v("--color-ring-track", "rgba(255,255,255,0.12)"),

        blue: "#3b82f6",
        violet: "#8b5cf6",
        orange: "#f59e0b",
        teal: "#14b8a6",
        yellow: "#ca8a04",
        red: "#ef4444",
      });
    };

    readColors();
    const observer = new MutationObserver(readColors);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return c;
}

/* Convert "#rrggbb" -> "rgba(r,g,b,a)" for gradients */
function withAlpha(hex, a) {
  const m = hex.replace("#", "");
  const n =
    m.length === 3
      ? m
          .split("")
          .map((x) => x + x)
          .join("")
      : m;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

/* Count-up hook (mirrors the mockup's cnt()) */
function useCountUp(target, run, dur = 1300) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(target * e);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, run, dur]);
  return Math.round(val).toLocaleString();
}

/* Fire `run` once the element scrolls into view */
function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ─────────────────────────────────────────────
   Decorative sparkline series — no time-series endpoint exists for these,
   so they remain ambient visuals under the role/team cards.
   ───────────────────────────────────────────── */
const DATA = {
  sparkStudents: [
    1180, 1290, 1360, 1455, 1520, 1610, 1685, 1740, 1790, 1820, 1845,
  ],
  sparkPros: [280, 318, 352, 391, 420, 455, 488, 512, 535, 552, 564],
  sparkTeams: [138, 145, 151, 156, 160, 164, 169, 172, 178, 182, 186],
  runsIndividual: [8, 10, 7, 11, 13, 10, 15, 12, 16, 13, 14],
  runsTeam: [4, 5, 4, 7, 8, 7, 9, 7, 10, 9, 9],
};

const TINTS = {
  teal: { bg: "rgba(20,184,166,.18)", fg: "#22d3ee" },
  blue: { bg: "rgba(59,130,246,.12)", fg: "#3b82f6" },
  violet: { bg: "rgba(139,92,246,.12)", fg: "#8b5cf6" },
  orange: { bg: "rgba(245,158,11,.12)", fg: "#f59e0b" },
  red: { bg: "rgba(239,68,68,.12)", fg: "#ef4444" },
};

/* ── Small building blocks ───────────────────── */

function SectionLabel({ children }) {
  return (
    <div className="text-[10.5px] tracking-[0.22em] uppercase font-semibold text-(--color-text-muted) font-mono">
      {children}
    </div>
  );
}

function Card({ children, className = "", style, ...rest }) {
  return (
    <div className={`qv-card qv-noise ${className}`} style={style} {...rest}>
      {children}
    </div>
  );
}

/* Pretty, actionable error state shown inside any section's Card */
function SectionError({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center text-center gap-3 px-5 py-9">
      <div className="w-11 h-11 rounded-full flex items-center justify-center bg-red-500/10 border border-red-500/20">
        <AlertTriangle size={20} className="text-red-400" />
      </div>
      <div>
        <div className="text-[13px] font-semibold text-(--color-text-primary)">
          Couldn’t load this section
        </div>
        <div className="text-[12px] text-(--color-text-muted) mt-0.5">
          {message || "Something went wrong fetching the data."}
        </div>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold text-(--color-primary) bg-(--color-primary)/8 border border-(--color-primary)/20 hover:bg-(--color-primary)/14 hover:-translate-y-px transition-all"
        >
          <RotateCw size={13} />
          Try again
        </button>
      )}
    </div>
  );
}

/* Tooltip preset shared across Chart.js instances */

function tipPreset(colors) {
  return {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderWidth: 1,
    titleColor: colors.textPrimary,
    bodyColor: colors.textSecondary,
    padding: 10,
    cornerRadius: 8,
    displayColors: true,
    boxWidth: 8,
    boxHeight: 8,
    boxPadding: 4,
    callbacks: {
      labelColor: (ctx) => {
        const ds = ctx.dataset;
        const solid =
          ds.borderColor ||
          (Array.isArray(ds.backgroundColor)
            ? ds.backgroundColor[ctx.dataIndex]
            : ds.backgroundColor);
        return {
          borderColor: solid,
          backgroundColor: solid,
          borderRadius: 2,
        };
      },
    },
  };
}

/* Glow plugin for arc-based charts (Doughnut) */
const arcGlowPlugin = {
  id: "arcGlow",
  afterDatasetsDraw(chart) {
    const active = chart.getActiveElements();
    if (!active.length) return;
    const { ctx } = chart;
    active.forEach(({ element, datasetIndex, index }) => {
      const ds = chart.data.datasets[datasetIndex];
      const color = Array.isArray(ds.backgroundColor)
        ? ds.backgroundColor[index]
        : ds.backgroundColor;
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = 22;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      element.draw(ctx);
      ctx.restore();
    });
  },
};

/* Glow plugin for line-based charts (Sparkline hover points) */
const pointGlowPlugin = {
  id: "pointGlow",
  afterDatasetsDraw(chart) {
    const active = chart.getActiveElements();
    if (!active.length) return;
    const { ctx } = chart;
    active.forEach(({ element, datasetIndex }) => {
      const ds = chart.data.datasets[datasetIndex];
      const color = ds.borderColor;
      const { x, y } = element;
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = 18;
      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.restore();
    });
  },
};

/* Hover-gated glow for line charts — only glows while the mouse is over
   the canvas, matching the MiniDonut's enter/leave behaviour. */
const lineGlowPlugin = {
  id: "lineGlow",
  afterDatasetsDraw(chart) {
    if (!chart._lineGlowHover) return;
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, i) => {
      const meta = chart.getDatasetMeta(i);
      if (meta.hidden || meta.type !== "line" || !meta.dataset) return;
      [8, 18].forEach((blur) => {
        ctx.save();
        ctx.shadowColor = dataset.borderColor;
        ctx.shadowBlur = blur;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        meta.dataset.draw(ctx, chart.chartArea);
        ctx.restore();
      });
    });
  },
  afterEvent(chart, args) {
    const was = chart._lineGlowHover;
    const now = args.event.type !== "mouseout";
    if (was !== now) {
      chart._lineGlowHover = now;
      chart.draw();
    }
  },
};

/* Glow plugin for bar charts — glows the hovered bar */
const barGlowPlugin = {
  id: "barGlow",
  afterDatasetsDraw(chart) {
    const active = chart.getActiveElements();
    if (!active.length) return;
    const { ctx } = chart;
    active.forEach(({ element, datasetIndex, index }) => {
      const ds = chart.data.datasets[datasetIndex];
      const color = Array.isArray(ds.backgroundColor)
        ? ds.backgroundColor[index]
        : ds.backgroundColor;
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = 22;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      element.draw(ctx);
      ctx.restore();
    });
  },
};

/* Sparkline line chart */
function Sparkline({ data, color, height = 48 }) {
  const [hovering, setHovering] = useState(false);
  const dataset = useMemo(
    () => ({
      labels: data.map((_, i) => i),
      datasets: [
        {
          data,
          fill: true,
          borderColor: color,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: color,
          pointHoverBorderColor: color,
          pointHoverBorderWidth: 2,
          tension: 0.4,
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(color, 0.1);
            const g = c.createLinearGradient(0, 0, 0, height);
            g.addColorStop(0, withAlpha(color, 0.22));
            g.addColorStop(1, withAlpha(color, 0));
            return g;
          },
        },
      ],
    }),
    [data, color, height],
  );
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    onHover: (_event, elements) => setHovering(elements.length > 0),
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: { x: { display: false }, y: { display: false } },
  };
  return (
    <div
      className="transition-[filter] duration-300"
      style={{
        height,
        filter: hovering
          ? `drop-shadow(0 0 6px ${color}) drop-shadow(0 0 14px ${withAlpha(color, 0.3)})`
          : "none",
      }}
    >
      <Line data={dataset} options={options} plugins={[pointGlowPlugin]} />
    </div>
  );
}

/* Doughnut split used in the role cards */
/* Doughnut split used in the role cards */
function MiniDonut({ values, colors, centerTop, centerBottom }) {
  const allZero = values.every((v) => !v);
  const [hovering, setHovering] = useState(false);
  const glowColor = colors[0];

  const data = {
    datasets: [
      {
        data: allZero ? [1] : values,
        backgroundColor: allZero ? [withAlpha(colors[0], 0.12)] : colors,
        hoverBackgroundColor: allZero
          ? [withAlpha(colors[0], 0.12)]
          : colors.map((c) => (c.startsWith("#") ? withAlpha(c, 0.95) : c)),
        borderWidth: 0,
        hoverBorderWidth: 2,
        hoverBorderColor: allZero ? "transparent" : colors,
        hoverOffset: 8,
      },
    ],
  };
  const options = {
    cutout: "66%",
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: 10 }, // ← CHANGE 1: add this line
    interaction: { mode: "nearest", intersect: true },
    onHover: (_event, elements) => setHovering(elements.length > 0),
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
  };
  return (
    <div
      // ← CHANGE 2: w-23 h-23  →  w-28 h-28
      className="relative w-28 h-28 shrink-0 transition-[filter] duration-300"
      style={{
        zIndex: 2,
        filter: hovering
          ? `drop-shadow(0 0 8px ${glowColor}) drop-shadow(0 0 18px ${withAlpha(glowColor, 0.35)})`
          : "none",
      }}
    >
      <Doughnut data={data} options={options} />
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="qv-num text-[17px] text-(--color-text-primary)">
          {centerTop}
        </span>
        <span className="text-[8.5px] text-(--color-text-muted)">
          {centerBottom}
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Sub-sections
   ───────────────────────────────────────────── */

function RoleCards({ colors }) {
  const [ref, inView] = useInView();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getUsersOrganizations();
        if (!cancelled) setData(normalizeUsersOrganizations(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const students = useCountUp(data?.students ?? 0, run);
  const pros = useCountUp(data?.professionals ?? 0, run);

  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>User Roles &amp; Organizations</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  if (loading || !data) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>User Roles &amp; Organizations</SectionLabel>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4.5">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className="p-5">
              <div className="animate-pulse flex flex-col gap-3">
                <div className="flex items-center gap-2.75">
                  <div className="w-10 h-10 rounded-full bg-white/5" />
                  <div className="h-3 w-28 rounded bg-white/5" />
                </div>
                <div className="h-20 rounded bg-white/5" />
              </div>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>User Roles &amp; Organizations</SectionLabel>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4.5">
        {/* Total Companies */}
        <Card
          className="p-5 qv-ping qv-glow"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
        >
          <div className="flex items-center gap-2.75 mb-3.5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-(--color-primary)/15">
              <Building2 size={18} className="text-(--color-primary)" />
            </div>
            <span className="text-[13px] leading-tight text-(--color-text-primary)">
              Total Companies
            </span>
          </div>
          <div className="flex items-center gap-4">
            <MiniDonut
              values={[data.companies.users, data.companies.admins]}
              colors={[colors.primary, withAlpha(colors.primary, 0.3)]}
              centerTop={data.companies.total}
              centerBottom="Total"
            />
            <div className="flex flex-col gap-2.25 text-xs flex-1">
              <div>
                <div className="flex items-center gap-1.5 text-(--color-text-secondary)">
                  <span className="w-2 h-2 rounded-xs bg-(--color-primary)" />
                  Users
                </div>
                <div className="qv-num text-[15px] ml-3.5 text-(--color-text-primary)">
                  {data.companies.users.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-(--color-text-secondary)">
                  <span
                    className="w-2 h-2 rounded-xs"
                    style={{ background: withAlpha(colors.primary, 0.3) }}
                  />
                  Admins
                </div>
                <div className="qv-num text-[15px] ml-3.5 text-(--color-text-primary)">
                  {data.companies.admins}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Total Universities */}
        <Card
          className="p-5 qv-ping qv-glow"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
        >
          <div className="flex items-center gap-2.75 mb-3.5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500/15">
              <GraduationCap size={18} className="text-blue-400" />
            </div>
            <span className="text-[13px] leading-tight text-(--color-text-primary)">
              Total Universities
            </span>
          </div>
          <div className="flex items-center gap-4">
            <MiniDonut
              values={[data.universities.students, data.universities.admins]}
              colors={[colors.blue, withAlpha(colors.blue, 0.3)]}
              centerTop={data.universities.total}
              centerBottom="Total"
            />
            <div className="flex flex-col gap-2.25 text-xs flex-1">
              <div>
                <div className="flex items-center gap-1.5 text-(--color-text-secondary)">
                  <span className="w-2 h-2 rounded-xs bg-blue-500" />
                  Students
                </div>
                <div className="qv-num text-[15px] ml-3.5 text-(--color-text-primary)">
                  {data.universities.students.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-(--color-text-secondary)">
                  <span
                    className="w-2 h-2 rounded-xs"
                    style={{ background: withAlpha(colors.blue, 0.3) }}
                  />
                  Admins
                </div>
                <div className="qv-num text-[15px] ml-3.5 text-(--color-text-primary)">
                  {data.universities.admins}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Individual Students */}
        <Card
          className="px-6.5 py-6 flex flex-col qv-ping p-5"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
        >
          <div className="flex items-center gap-2.75 mb-3.5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-orange-500/15">
              <UserRound size={18} className="text-orange-400" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              Individual Students
            </span>
          </div>
          <div className="qv-num text-[32px] mb-0.5 text-(--color-text-primary)">
            {students}
          </div>
          <div className="text-(--color-text-secondary) text-[11.5px]">
            Registered learners
          </div>
          <div className="mt-2.5">
            <Sparkline data={DATA.sparkStudents} color={colors.orange} />
          </div>
        </Card>

        {/* Professionals */}
        <Card
          className="px-6.5 py-6 flex flex-col qv-ping p-5"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
        >
          <div className="flex items-center gap-2.75 mb-3.5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-yellow-500/15">
              <Award size={18} className="text-yellow-500" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              Professionals
            </span>
          </div>
          <div className="qv-num text-[32px] mb-0.5 text-(--color-text-primary)">
            {pros}
          </div>
          <div className="text-(--color-text-secondary) text-[11.5px]">
            Certified members
          </div>
          <div className="mt-2.5">
            <Sparkline data={DATA.sparkPros} color={colors.yellow} />
          </div>
        </Card>
      </div>
    </section>
  );
}

function AccountTrend({ colors }) {
  const [ref, inView] = useInView(0.05);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getPlatformAnalytics();
        if (!cancelled) setData(normalizePlatformAnalytics(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const today = useCountUp(data?.newUsersToday ?? 0, run);

  const chartData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.labels,
      datasets: [
        {
          data: data.series,
          fill: true,
          borderColor: colors.primary,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: colors.primary,
          pointHoverBorderColor: colors.surface,
          pointHoverBorderWidth: 2,
          tension: 0.4,
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(colors.primary, 0.1);
            const g = c.createLinearGradient(0, 0, 0, 200);
            g.addColorStop(0, withAlpha(colors.primary, 0.2));
            g.addColorStop(1, withAlpha(colors.primary, 0));
            return g;
          },
        },
      ],
    };
  }, [data, colors]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tipPreset(colors),
        mode: "index",
        intersect: false,
        callbacks: {
          ...tipPreset(colors).callbacks,
          title: (items) => items[0]?.label ?? "",
          label: (ctx) =>
            " " +
            ctx.parsed.y.toLocaleString() +
            (ctx.parsed.y === 1 ? " new account" : " new accounts"),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxTicksLimit: 6, color: colors.textMuted },
      },
      y: {
        grid: { color: withAlpha(colors.textPrimary, 0.04) },
        ticks: {
          color: colors.textMuted,
          callback: (v) => (v >= 1000 ? (v / 1000).toFixed(1) + "k" : v),
        },
      },
    },
  };

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Platform Analytics</SectionLabel>
      </div>
      <Card className="p-5.5">
        <div className="flex justify-between items-center mb-1.5 gap-3 flex-wrap">
          <div>
            <h3 className="text-[13.5px] font-semibold text-(--color-text-primary)">
              New Account Creation — 30 Day Trend
            </h3>
            <p className="text-xs text-(--color-text-muted) mt-0.5">
              Daily new account signups across all platforms
            </p>
          </div>
          <div className="text-right">
            <div className="qv-num text-[22px] text-(--color-primary)">
              {error ? "—" : today}
            </div>
            <div className="text-[10.5px] text-(--color-text-muted) font-mono">
              TODAY
            </div>
          </div>
        </div>
        <div className="h-70 mt-2">
          {error ? (
            <SectionError message={error} onRetry={retry} />
          ) : loading || !chartData ? (
            <div className="h-full w-full rounded-lg bg-white/5 animate-pulse" />
          ) : (
            <Line
              data={chartData}
              options={options}
              plugins={[lineGlowPlugin]}
            />
          )}
        </div>
      </Card>
    </section>
  );
}

function Infrastructure({ colors }) {
  const [ref, inView] = useInView(0.1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getInfrastructure();
        if (!cancelled) setData(normalizeInfrastructure(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const totalBoxes = useCountUp(data?.totalBoxes ?? 0, run);
  const withTop = useCountUp(data?.withTopologies ?? 0, run);
  const totalTop = useCountUp(data?.totalTopologies ?? 0, run);
  const [ringHover, setRingHover] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Once opened, the panel stays mounted and is only hidden on collapse —
  // re-mounting it would re-request the catalog on every toggle.
  const [hasOpened, setHasOpened] = useState(false);

  const toggleExpanded = () => {
    setHasOpened(true);
    setExpanded((v) => !v);
  };

  const ringData = useMemo(() => {
    if (!data) return null;
    return {
      labels: ["With topologies", "Unassigned"],
      datasets: [
        {
          data: [data.coverage, 100 - data.coverage],
          backgroundColor: [colors.primary, colors.ringTrack],
          borderColor: "transparent",
          borderWidth: 0,
          hoverOffset: 0,
        },
      ],
    };
  }, [data, colors]);

  const ringOptions = {
    cutout: "76%",
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    animation: { animateRotate: true, duration: 1200 },
  };

  const stat = (Icon, label, value) => (
    <div className="px-4 py-3.5 rounded-xl border border-(--color-border) bg-(--color-bg)/60">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} className="text-(--color-primary)" />
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-(--color-text-muted) font-mono">
          {label}
        </span>
      </div>
      <div className="qv-num text-[26px] text-(--color-text-primary)">
        {value}
      </div>
    </div>
  );

  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Infrastructure</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  if (loading || !data) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Infrastructure</SectionLabel>
        </div>

        <Card className="px-7.5 py-6.5 flex items-center gap-8.5 flex-wrap ">
          <div className="w-37.5 h-37.5 rounded-full bg-white/5 animate-pulse shrink-0" />
          <div className="flex-1 min-w-65">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-20 rounded-xl bg-white/5 animate-pulse"
                />
              ))}
            </div>
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Infrastructure</SectionLabel>
      </div>
      <Card
        className="px-7.5 py-6.5 flex items-center gap-8.5 flex-wrap qv-ping cursor-pointer"
        style={{ "--ping-x": "100%", "--ping-y": "100%" }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggleExpanded}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <div className="absolute top-4 right-4 flex items-center gap-1.5 text-[11px] font-semibold text-(--color-text-muted)">
          {expanded ? "Hide boxes" : "View boxes"}
          <ChevronDown
            size={14}
            className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
          />
        </div>
        <div
          className="relative w-37.5 h-37.5 shrink-0 transition-[filter] duration-300"
          onMouseEnter={() => setRingHover(true)}
          onMouseLeave={() => setRingHover(false)}
          style={{
            filter: ringHover
              ? `drop-shadow(0 0 8px ${colors.primary}) drop-shadow(0 0 18px ${withAlpha(colors.primary, 0.35)})`
              : "none",
          }}
        >
          <Doughnut data={ringData} options={ringOptions} />
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="qv-num text-[30px] text-(--color-primary)">
              {data.coverage}%
            </span>
            <span className="text-[9px] tracking-[0.12em] uppercase text-(--color-text-secondary) font-mono mt-0.5">
              coverage
            </span>
          </div>
        </div>
        <div className="flex-1 min-w-full sm:min-w-65">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
            {stat(Network, "Total Boxes", totalBoxes)}
            {stat(LayoutDashboard, "With Topologies", withTop)}
            {stat(Database, "Total Topologies", totalTop)}
          </div>
          <div className="mt-3.5 text-xs text-(--color-text-secondary) flex items-center gap-2 flex-wrap">
            <span className="w-2.25 h-2.25 rounded-xs bg-(--color-primary)" />
            {data.withTopologies} of {data.totalBoxes} boxes have topologies
            assigned
            <span className="text-(--color-text-muted)">·</span>
            <span
              className="w-2.25 h-2.25 rounded-xs"
              style={{ background: withAlpha(colors.textPrimary, 0.08) }}
            />
            {data.unassigned} unassigned
          </div>
        </div>
      </Card>

      {hasOpened && (
        <div className={expanded ? "" : "hidden"}>
          <BoxLinksPanel />
        </div>
      )}
    </section>
  );
}

function TopologyTable() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getTopologyUsage(5);
        if (!cancelled) setData(normalizeTopologyUsage(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  return (
    <div>
      <div className="mb-4">
        <SectionLabel>Topology Usage Statistics</SectionLabel>
      </div>
      <Card>
        <div className="flex justify-between items-center px-4.5 py-4 border-b border-(--color-border)">
          <h3 className="text-xs font-semibold text-(--color-text-secondary) flex items-center gap-2">
            Showing
            <span className="inline-flex items-center px-2.25 py-0.75 rounded-md text-[11px] font-semibold bg-(--color-primary)/10 text-(--color-primary) border border-(--color-primary)/30">
              Top {data?.length ?? 5}
            </span>
            organizations
          </h3>
        </div>

        {error ? (
          <SectionError message={error} onRetry={retry} />
        ) : loading || !data ? (
          <div className="p-4.5 flex flex-col gap-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-9 rounded bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : data.length === 0 ? (
          <div className="px-4.5 py-8 text-[13px] text-(--color-text-muted) text-center">
            No organizations to display.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {["Organizations", "Active Topologies", "Boxes"].map((h) => (
                    <th
                      key={h}
                      className="text-[11.5px] font-bold text-(--color-text-primary) px-4.5 py-2.5 text-left"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row) => {
                  const t = TINTS[row.tint];
                  return (
                    <tr
                      key={row.id}
                      className="hover:bg-white/2.5 transition-colors"
                    >
                      <td className="px-4.5 py-2.5 border-t border-(--color-border)">
                        <div className="flex items-center gap-2.25">
                          <span
                            className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[10px] font-bold shrink-0"
                            style={{ background: t.bg, color: t.fg }}
                          >
                            {row.initials}
                          </span>
                          <span className="text-[13px] text-(--color-text-primary) font-medium">
                            {row.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4.5 py-2.5 border-t border-(--color-border)">
                        <span className="text-(--color-primary) font-semibold">
                          {row.topologies}
                        </span>
                        <span className="text-[11px] ml-1 text-(--color-text-secondary)">
                          topologies
                        </span>
                      </td>
                      <td className="px-4.5 py-2.5 border-t border-(--color-border)">
                        <span className="text-(--color-primary) font-semibold">
                          {row.boxes}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function ActivityDeployments({ colors }) {
  const [ref, inView] = useInView(0.05);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getActivityDeployments(5);
        if (!cancelled) setData(normalizeActivityDeployments(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const requested = useCountUp(data?.summary.requested ?? 0, run);
  const [ringHover, setRingHover] = useState(false);

  const flowData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.daily.labels,
      datasets: [
        {
          label: "Completed",
          data: data.daily.completed,
          fill: true,
          borderColor: colors.primary,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.4,
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(colors.primary, 0.1);
            const g = c.createLinearGradient(0, 0, 0, 160);
            g.addColorStop(0, withAlpha(colors.primary, 0.18));
            g.addColorStop(1, withAlpha(colors.primary, 0));
            return g;
          },
        },
        {
          label: "Failed",
          data: data.daily.failed,
          fill: true,
          borderColor: colors.red,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.4,
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(colors.red, 0.1);
            const g = c.createLinearGradient(0, 0, 0, 160);
            g.addColorStop(0, withAlpha(colors.red, 0.18));
            g.addColorStop(1, withAlpha(colors.red, 0));
            return g;
          },
        },
      ],
    };
  }, [data, colors]);

  const flowOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: { ...tipPreset(colors), mode: "index", intersect: false },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: colors.textMuted, maxTicksLimit: 7 },
      },
      y: {
        grid: { color: withAlpha(colors.textPrimary, 0.04) },
        ticks: { color: colors.textMuted },
      },
    },
  };

  const ringData = useMemo(() => {
    if (!data) return null;
    return {
      datasets: [
        {
          data: [data.summary.successRate, 100 - data.summary.successRate],
          backgroundColor: [colors.primary, colors.ringTrack],
          borderColor: "transparent",
          borderWidth: 0,
          hoverOffset: 0,
        },
      ],
    };
  }, [data, colors]);

  const ringOptions = {
    cutout: "78%",
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    animation: { animateRotate: true, duration: 1400 },
  };

  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Activity &amp; Deployments</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  const ready = !loading && !!data;

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Activity &amp; Deployments</SectionLabel>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {/* Flow chart */}
        <Card className="p-5.5 xl:col-span-2">
          <div className="flex justify-between items-center mb-1 gap-3 flex-wrap">
            <div>
              <h3 className="text-[13.5px] font-semibold text-(--color-text-primary)">
                Box Requests / Creation — Daily Breakdown
              </h3>
              <p className="text-xs text-(--color-text-muted) mt-0.5">
                Completed vs failed runs over last 30 days
              </p>
            </div>
            <div className="flex gap-3.5 text-xs">
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2.5 h-0.75 rounded-xs bg-(--color-primary)" />
                Completed
              </span>
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2.5 h-0.75 rounded-xs bg-red-500" />
                Failed
              </span>
            </div>
          </div>
          <div className="h-46.25 mt-3">
            {ready ? (
              <Line
                data={flowData}
                options={flowOptions}
                plugins={[lineGlowPlugin]}
              />
            ) : (
              <div className="h-full w-full rounded-lg bg-white/5 animate-pulse" />
            )}
          </div>
        </Card>

        {/* Box requests ring */}
        <Card
          className="p-5.5 qv-ping"
          style={{ "--ping-x": "100%", "--ping-y": "100%" }}
        >
          <h3 className="text-[13.5px] font-semibold mb-4 text-(--color-text-primary)">
            Box Requests
          </h3>
          <div
            className="relative w-27.5 h-27.5 mx-auto mb-4 transition-[filter] duration-300"
            onMouseEnter={() => setRingHover(true)}
            onMouseLeave={() => setRingHover(false)}
            style={{
              filter: ringHover
                ? `drop-shadow(0 0 8px ${colors.primary}) drop-shadow(0 0 18px ${withAlpha(colors.primary, 0.35)})`
                : "none",
            }}
          >
            {ready ? (
              <>
                <Doughnut data={ringData} options={ringOptions} />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="qv-num text-[22px] text-(--color-primary)">
                    {data.summary.successRate}%
                  </span>
                  <span className="text-[9.5px] text-(--color-text-muted) font-mono uppercase tracking-[0.08em]">
                    Success Rate
                  </span>
                </div>
              </>
            ) : (
              <div className="w-full h-full rounded-full bg-white/5 animate-pulse" />
            )}
          </div>
          <div className="flex flex-col gap-2.25">
            <div className="flex justify-between items-center text-[12.5px] pb-2 border-b border-(--color-border)">
              <span className="text-(--color-text-secondary) flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-xs bg-(--color-text-secondary) shrink-0" />
                Boxes Requested
              </span>
              <span className="font-semibold text-(--color-text-primary)">
                {ready ? requested : "—"}
              </span>
            </div>
            <div className="flex justify-between items-center text-[12.5px] pb-2 border-b border-(--color-border)">
              <span className="text-(--color-text-secondary) flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-xs bg-(--color-primary) shrink-0" />
                Completed Successfully
              </span>
              <span className="font-semibold text-(--color-primary)">
                {ready ? data.summary.completed : "—"}
              </span>
            </div>
            <div className="flex justify-between items-center text-[12.5px]">
              <span className="text-(--color-text-secondary) flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-xs bg-red-500 shrink-0" />
                Failed
              </span>
              <span className="font-semibold text-red-500">
                {ready ? data.summary.failed : "—"}
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Hackbox by org */}
      <div className="mt-5">
        <Card className="p-5.5">
          <div className="mb-1">
            <h3 className="text-[13.5px] font-semibold text-(--color-text-primary)">
              Hackbox Deployments by Org
            </h3>
            <p className="text-xs text-(--color-text-muted) mt-0.5">
              Top organizations by number of created hackboxes
            </p>
          </div>
          <div className="h-50 mt-3.5">
            {ready ? (
              <HackboxBar colors={colors} topOrgs={data.topOrgs} />
            ) : (
              <div className="h-full w-full rounded-lg bg-white/5 animate-pulse" />
            )}
          </div>
        </Card>
      </div>
    </section>
  );
}

function HackboxBar({ colors, topOrgs }) {
  const palette = [
    withAlpha(colors.blue, 0.55),
    withAlpha(colors.primary, 0.55),
    withAlpha(colors.primary, 0.4),
    withAlpha(colors.orange, 0.5),
    withAlpha(colors.violet, 0.5),
  ];
  const hoverPalette = [
    withAlpha(colors.blue, 0.85),
    withAlpha(colors.primary, 0.85),
    withAlpha(colors.primary, 0.7),
    withAlpha(colors.orange, 0.8),
    withAlpha(colors.violet, 0.8),
  ];

  const data = {
    labels: topOrgs.labels,
    datasets: [
      {
        data: topOrgs.values,
        backgroundColor: topOrgs.values.map(
          (_, i) => palette[i % palette.length],
        ),
        hoverBackgroundColor: topOrgs.values.map(
          (_, i) => hoverPalette[i % hoverPalette.length],
        ),
        borderRadius: 5,
        borderSkipped: false,
      },
    ],
  };
  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tipPreset(colors),
        callbacks: {
          ...tipPreset(colors).callbacks,
          label: (ctx) => ` ${ctx.parsed.x} hackboxes`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: withAlpha(colors.textPrimary, 0.04) },
        ticks: { color: colors.textMuted },
      },
      y: {
        grid: { display: false },
        ticks: { color: colors.textSecondary, font: { weight: "500" } },
      },
    },
  };
  return <Bar data={data} options={options} plugins={[barGlowPlugin]} />;
}

function PendingApprovals() {
  const [open, setOpen] = useState(false);
  const VISIBLE = 4;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getPendingApprovals(20);
        if (!cancelled) setData(normalizePendingApprovals(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const tier = (d) =>
    d <= 3
      ? { color: "var(--color-primary)" }
      : d <= 7
        ? { color: "#f59e0b" }
        : { color: "#ef4444" };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const shown = open ? items : items.slice(0, VISIBLE);
  const extra = items.length - VISIBLE;

  return (
    <section>
      <div className="flex items-center gap-2.5 mb-4">
        <SectionLabel>Pending Approvals</SectionLabel>
        {!loading && !error && (
          <span className="inline-flex items-center px-2.25 py-0.75 rounded-md text-[11px] font-semibold bg-orange-500/12 text-orange-500 border border-orange-500/30">
            {total} in queue
          </span>
        )}
      </div>
      <Card className="p-0 overflow-hidden">
        <div className="grid grid-cols-[1.6fr_1.4fr_1fr] gap-3 px-5.5 py-3.25 border-b border-(--color-border) text-[10px] font-semibold uppercase tracking-widest text-(--color-text-muted) font-mono">
          <span>Organization / User</span>
          <span>Box</span>
          <span className="text-right">In Queue</span>
        </div>

        {error ? (
          <SectionError message={error} onRetry={retry} />
        ) : loading || !data ? (
          <div className="p-5.5 flex flex-col gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-6 rounded bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="px-5.5 py-8 text-[13px] text-(--color-text-muted) text-center">
            Nothing awaiting approval.
          </div>
        ) : (
          <>
            <div>
              {shown.map((r) => {
                const t = tier(r.days);
                return (
                  <div
                    key={r.id}
                    className="grid grid-cols-[1.6fr_1.4fr_1fr] gap-3 items-center px-5.5 py-3.25 text-[12.5px] hover:bg-white/2 transition-colors border-b border-(--color-border) last:border-b-0"
                  >
                    <span className="font-semibold text-(--color-text-primary)">
                      {r.company}
                    </span>
                    <span className="text-(--color-text-secondary)">
                      {r.box}
                    </span>
                    <span
                      className="text-right qv-num font-semibold"
                      style={{ color: t.color }}
                    >
                      {r.queueLabel}
                    </span>
                  </div>
                );
              })}
            </div>
            {extra > 0 && (
              <div className="flex justify-center p-3.5 border-t border-(--color-border)">
                <button
                  onClick={() => setOpen((o) => !o)}
                  className="inline-flex items-center gap-2 pl-4 pr-2 py-1.75 rounded-full text-xs font-semibold text-(--color-primary) bg-(--color-primary)/6 border border-(--color-primary)/15 hover:bg-(--color-primary)/10 hover:-translate-y-px transition-all"
                >
                  <span>{open ? "Show less" : "Show more"}</span>
                  {!open && (
                    <span className="inline-flex items-center justify-center min-w-5.5 h-5.5 px-1.5 rounded-full bg-(--color-primary)/15 text-(--color-primary) qv-num text-[11px]">
                      {extra}
                    </span>
                  )}
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </section>
  );
}

function TeamChallenges({ colors }) {
  const [ref, inView] = useInView(0.1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getTeamChallenges();
        if (!cancelled) setData(normalizeTeamChallenges(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const totalTeams = useCountUp(data?.totalTeams ?? 0, run);
  const activeRuns = useCountUp(data?.activeRuns ?? 0, run);

  const runsData = {
    labels: DATA.runsIndividual.map((_, i) => i),
    datasets: [
      {
        label: "Individual",
        data: DATA.runsIndividual,
        fill: true,
        borderColor: colors.blue,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 3,
        tension: 0.4,
        backgroundColor: (ctx) => {
          const { ctx: c, chartArea } = ctx.chart;
          if (!chartArea) return withAlpha(colors.blue, 0.1);
          const g = c.createLinearGradient(0, 0, 0, 48);
          g.addColorStop(0, withAlpha(colors.blue, 0.18));
          g.addColorStop(1, withAlpha(colors.blue, 0));
          return g;
        },
      },
      {
        label: "Team",
        data: DATA.runsTeam,
        fill: true,
        borderColor: colors.primary,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 3,
        tension: 0.4,
        backgroundColor: (ctx) => {
          const { ctx: c, chartArea } = ctx.chart;
          if (!chartArea) return withAlpha(colors.primary, 0.1);
          const g = c.createLinearGradient(0, 0, 0, 48);
          g.addColorStop(0, withAlpha(colors.primary, 0.18));
          g.addColorStop(1, withAlpha(colors.primary, 0));
          return g;
        },
      },
    ],
  };
  const runsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: { x: { display: false }, y: { display: false } },
  };

  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Team Challenges</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
        <div className="mt-5">
          <TopCompanies colors={colors} />
        </div>
      </section>
    );
  }

  const ready = !loading && !!data;

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Team Challenges</SectionLabel>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4.5 mb-5">
        {/* Total Teams */}
        <Card
          className="px-6.5 py-6 flex flex-col qv-ping p-5"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4.5 min-w-0">
              <div className="w-13 h-13 rounded-[14px] flex items-center justify-center bg-(--color-primary)/15 shrink-0">
                <Users size={26} className="text-(--color-primary)" />
              </div>
              <div className="min-w-0">
                <div className="text-[10.5px] font-semibold uppercase tracking-widest text-(--color-text-muted) mb-1.5 font-mono">
                  Total Teams
                </div>
                <div className="qv-num text-[38px] leading-none text-(--color-text-primary)">
                  {ready ? totalTeams : "—"}
                </div>
                <div className="text-xs text-(--color-text-secondary) mt-1.5">
                  across all organizations
                </div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[22px] font-semibold qv-num text-blue-400">
                {ready ? `+${data.newThisMonth}` : "—"}
              </div>
              <div className="text-[10px] text-(--color-text-muted) font-mono uppercase tracking-[0.08em] mt-0.75">
                New this month
              </div>
            </div>
          </div>
          <div className="h-11 mt-4">
            <Sparkline
              data={DATA.sparkTeams}
              color={colors.primary}
              height={44}
            />
          </div>
        </Card>

        {/* Active Runs */}
        <Card
          className="px-6.5 py-6 flex flex-col qv-ping p-5"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4.5 min-w-0">
              <div className="w-13 h-13 rounded-[14px] flex items-center justify-center bg-(--color-primary)/15 shrink-0">
                <Zap size={26} className="text-(--color-primary)" />
              </div>
              <div className="min-w-0">
                <div className="text-[10.5px] font-semibold uppercase tracking-widest text-(--color-text-muted) mb-1.5 font-mono">
                  Active Runs
                </div>
                <div className="qv-num text-[38px] leading-none text-(--color-primary)">
                  {ready ? activeRuns : "—"}
                </div>
                <div className="flex items-center gap-3.5 mt-2 text-xs text-(--color-text-secondary) flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-xs bg-blue-500 shrink-0" />
                    <span className="font-semibold text-(--color-text-primary)">
                      {ready ? data.individual : "—"}
                    </span>
                    individual
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-xs bg-(--color-primary) shrink-0" />
                    <span className="font-semibold text-(--color-text-primary)">
                      {ready ? data.team : "—"}
                    </span>
                    team
                  </span>
                </div>
                <div className="text-xs text-(--color-text-secondary) mt-1.5">
                  total boxes running (individual + team)
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-(--color-primary)/10 border border-(--color-primary)/25 shrink-0">
              <StatusDot variant="live" size={7} />
              <span className="text-[11px] font-semibold text-(--color-primary) font-mono uppercase tracking-[0.06em]">
                Live
              </span>
            </div>
          </div>
          <div className="h-11 mt-4">
            <Line
              data={runsData}
              options={runsOptions}
              plugins={[lineGlowPlugin]}
            />
          </div>
        </Card>
      </div>

      <TopCompanies colors={colors} />
    </section>
  );
}

function TopCompanies({ colors }) {
  const [ref, inView] = useInView(0.2);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getTopPerformingCompanies(5);
        if (!cancelled) setData(normalizeTopPerformingCompanies(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const medal = ["#f59e0b", "#9ca3af", "#b45309"];

  const pill = (color, pct, label) => (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.75 rounded-md text-[11px]"
      style={{
        background: withAlpha(color, 0.1),
        border: `1px solid ${withAlpha(color, 0.2)}`,
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-xs inline-block shrink-0"
        style={{ background: color }}
      />
      <span className="qv-num font-semibold" style={{ color }}>
        {Math.round(pct)}%
      </span>
      <span className="text-(--color-text-muted)">{label}</span>
    </span>
  );

  return (
    <div ref={ref}>
      <div className="flex items-center gap-2 mt-1 mb-3.5">
        <Trophy size={18} className="text-orange-500" />
        <div className="text-xs font-semibold text-(--color-text-secondary)">
          Top Performing Companies
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-2.5 pl-1 flex-wrap">
        {[
          ["Easy", colors.primary],
          ["Medium", colors.orange],
          ["Hard", colors.red],
        ].map(([label, color]) => (
          <span
            key={label}
            className="flex items-center gap-1.5 text-[11.5px] text-(--color-text-secondary)"
          >
            <span
              className="w-2.5 h-2.5 rounded-[3px] inline-block"
              style={{ background: color }}
            />
            {label}
          </span>
        ))}
      </div>

      <Card className="px-1.5 py-2">
        {error ? (
          <SectionError message={error} onRetry={retry} />
        ) : loading || !data ? (
          <div className="px-4 py-2 flex flex-col gap-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-12 rounded-[10px] bg-white/5 animate-pulse"
              />
            ))}
          </div>
        ) : data.length === 0 ? (
          <div className="px-4 py-8 text-[13px] text-(--color-text-muted) text-center">
            No company performance data yet.
          </div>
        ) : (
          data.map((r, i) => {
            const rankColor = medal[i] || "var(--color-text-muted)";
            const rankBg =
              i < 3 ? "rgba(245,158,11,.10)" : "rgba(255,255,255,.04)";
            return (
              <div
                key={r.id}
                className="flex items-center gap-3.5 px-4 py-3.25 rounded-[10px] hover:bg-white/3 transition-colors"
              >
                <span
                  className="inline-flex items-center justify-center w-7 h-7 rounded-lg qv-num font-bold text-[13px] shrink-0"
                  style={{ background: rankBg, color: rankColor }}
                >
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center mb-2 gap-2.5 flex-wrap">
                    <span className="font-semibold text-[13px] text-(--color-text-primary) truncate">
                      {r.company}
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                      {pill(colors.primary, r.eShare, "easy")}
                      {pill(colors.orange, r.mShare, "medium")}
                      {pill(colors.red, r.hShare, "hard")}
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <div className="flex-1 h-1.75 rounded flex overflow-hidden bg-white/5">
                      <div
                        className="h-full transition-[width] duration-1000 ease-out"
                        style={{
                          width: run ? `${r.eShare}%` : 0,
                          background: colors.primary,
                        }}
                      />
                      <div
                        className="h-full transition-[width] duration-1000 ease-out"
                        style={{
                          width: run ? `${r.mShare}%` : 0,
                          background: colors.orange,
                          transitionDelay: "0.12s",
                        }}
                      />
                      <div
                        className="h-full transition-[width] duration-1000 ease-out"
                        style={{
                          width: run ? `${r.hShare}%` : 0,
                          background: colors.red,
                          transitionDelay: "0.24s",
                        }}
                      />
                    </div>
                    <span className="qv-num font-semibold text-xs text-(--color-text-secondary) shrink-0 min-w-19.5 text-right">
                      {r.pct}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Page
   ───────────────────────────────────────────── */
export default function PlatformAdminDashboard() {
  const colors = useThemeColors();
  const { user } = useAuth();
  const displayName = user?.name || user?.username || "Admin";

  return (
    <AdminLayout>
      <style>{`.qv-card.qv-glow, .qv-ping.qv-glow { overflow: visible !important; }`}</style>
      <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-7">
        {/* Greeting */}
        <div>
          <div className="text-[10.5px] tracking-[0.22em] uppercase text-(--color-text-muted) font-mono">
            Platform Overview
          </div>
          <h1 className="qv-serif mt-2 text-[30px] sm:text-[40px] leading-[1.05] font-medium tracking-tight text-(--color-text-primary)">
            Welcome back,{" "}
            <span className="text-(--color-primary)">{displayName}</span>.
          </h1>
        </div>

        {colors && (
          <>
            <RoleCards colors={colors} />
            <AccountTrend colors={colors} />
            <Infrastructure colors={colors} />
            <TopologyTable />
            <ActivityDeployments colors={colors} />
            <PendingApprovals />
            <TeamChallenges colors={colors} />
          </>
        )}
      </div>
    </AdminLayout>
  );
}
